/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import { addMessageListener, MessagingContract, sendMessage } from "@hcecbp/uiintegrationcomponent";

import RbmRoutes from "./RbmRoutes";

import { ViewTypeProps, AppContextPayload, OrgListProps } from "../../models";

import { getThemeName } from "../../utils/utility";

import { ConfigurationVariables } from "../../config";
import { IAM_PROJECT_ID_SESSION_TOKEN_STORAGE_NAME, RBM_ACCESS_TOKEN_STORAGE_NAME } from "../../utils/constants";
import { useGetOrgListsQuery } from "../../services/rbm-api";

import { IBuildOrganisation } from "../../models/devicelist";
import { RootState } from "../../store";
import { setOrganizations, setRBMDetails, setDeviceListUpdated, setSiteContext } from "../../actions";
import { parseSiteContext } from "../../utils/siteContext";
import CustomPageHeader from "../../reusablecomponents/Header";

const RbmApplication = (props: { type: string }) => {
    console.log("RBM Application: ", props);
    const { i18n } = useTranslation();
    const dispatch = useDispatch();

    const [tempTheme, setTempTheme] = useState("0");
    const [tempLocale, setTempLocale] = useState("en-US");

    const { selectedOrg, rbmAccessToken, customerType, gatewayType, siteView } = useSelector((state: RootState) => state.global);

    const lcbsQueryParam = /^(dvm|ebi[\s_-]?platform)$/i.test(String(gatewayType || ""))
        ? "?fetchOnPrem=true"
        : "";

    const { data: orgDataLists, refetch: refetchOrgLists } = useGetOrgListsQuery(lcbsQueryParam, {
        skip: !rbmAccessToken,
        refetchOnMountOrArgChange: true
    });

    useEffect(() => {
        if (rbmAccessToken) {
            // Force a network hit once token/context is ready so request is visible in DevTools.
            refetchOrgLists();
        }
    }, [rbmAccessToken, refetchOrgLists]);

    // Function to handle 'appContext' messages received from the parent window
    function handleAppContext(payload: AppContextPayload) {
        try {
            const tempViewType: ViewTypeProps = JSON.parse(payload.viewType);
            console.log('View Type:', tempViewType);

            // Refresh-only message: must not overwrite theme, locale, org or token below.
            if (tempViewType.refreshGatewayList) {
                dispatch(setDeviceListUpdated({ deviceListUpdated: true }));
                return;
            }

            setTempTheme(tempViewType.theme);
            setTempLocale(tempViewType.locale);

            // Site context first so the first list query is already site-scoped.
            // Cleared when absent so a reused iframe never keeps a previous site's scope.
            dispatch(setSiteContext(parseSiteContext(tempViewType) ?? {}));

            dispatch(setRBMDetails({
                selectedOrg: tempViewType.selectedOrg || '',
                rbmAccessToken: tempViewType.rbmAccessToken || '',
                gatewayType: tempViewType.gatewayType || ''
            }));
            if (tempViewType.customerType && tempViewType.customerOrgId) {
                dispatch({
                    type: 'SET_CUSTOMER_TYPE', payload: {
                        customerType: tempViewType.customerType,
                        customerOrgId: tempViewType.customerOrgId,
                        siteDeviceId: tempViewType.siteDeviceId || '',
                        siteType: tempViewType.siteType || false
                    }
                });
            }

            sessionStorage.setItem(RBM_ACCESS_TOKEN_STORAGE_NAME, tempViewType.rbmAccessToken || '');
            sessionStorage.setItem(IAM_PROJECT_ID_SESSION_TOKEN_STORAGE_NAME, tempViewType.selectedOrg || '');
        } catch (error) {
            console.error('Error parsing app context:', error);
        }
    }

    // // Function to process messages which type is 'appContext'
    const processMessage = (message: MessagingContract) => {
        console.log('Received message in App.tsx:', message);

        if (message.type === "appContext") {
            handleAppContext(message.payload as AppContextPayload);
        }
    };

    useEffect(() => {
        const event = addMessageListener(ConfigurationVariables.RBM_PORTAL_HOST_URL, processMessage);

        return () => {
            if (event) {
                event.dispose();
            }
        };
    }, []);

    useEffect(() => {
        // Access the <html> tag  
        sendMessage(
            ConfigurationVariables.RBM_PORTAL_HOST_URL,
            {
                type: "appContext",
                payload: {
                    viewType: JSON.stringify({ iotLoaded: true })
                }
            },
            window.parent
        );
    }, []);

    useEffect(() => {
        // Lets the parent sidebar background show through (see global.scss).
        if (siteView !== 'assignPanel') {
            return undefined;
        }

        const root = document.documentElement;
        root.classList.add('assign-panel-view');

        return () => {
            root.classList.remove('assign-panel-view');
        };
    }, [siteView]);

    useEffect(() => {
        const gatewayElement = document.documentElement;

        const applyTheme = () => {
            console.log('Applying theme:', tempTheme);

            // Remove both classes before adding the appropriate one
            gatewayElement.classList.remove('light', 'dark');

            if (tempTheme === "0") {
                gatewayElement.classList.add('dark');
            } else {
                gatewayElement.classList.add('light');
            }

            console.log('Current class list:', gatewayElement.className);
        };

        applyTheme();

        const observer = new MutationObserver(() => {
            if (gatewayElement.classList.contains('dark') && gatewayElement.classList.contains('light')) {
                applyTheme();
            } else if (!gatewayElement.classList.contains('dark') && !gatewayElement.classList.contains('light')) {
                applyTheme();
            }
        });

        observer.observe(gatewayElement, { attributes: true, attributeFilter: ['class'] });

        return () => {
            observer.disconnect();
        };
    }, [tempTheme]);

    useEffect(() => {
        if (tempLocale) {
            i18n.changeLanguage(tempLocale.replace("-", ""));
        }
    }, [tempLocale]);

    useEffect(() => {

        if (orgDataLists?.resources?.length > 0) {
            const OrgSILists: OrgListProps[] = [];

            orgDataLists?.resources?.forEach((item: IBuildOrganisation) => {
                // Filter out customer organizations under SI Org
                if (item.servedBy === selectedOrg) {
                    OrgSILists.push({
                        text: item.name,
                        value: item.id
                    });
                }
                // if (!item.zuoraAccountNo) {
                //     nonSIOrgLists.push({
                //         text: item.name,
                //         value: item.id
                //     });
                // }
            });

            const tempOrgLists: OrgListProps[] = OrgSILists.sort((a, b) => a.text.localeCompare(b.text));
            dispatch(setOrganizations({ orgLists: tempOrgLists }));
        }
    }, [orgDataLists, selectedOrg]);

    const getClsName = () => {
        let clsName = `theme-${getThemeName(tempTheme)} `;

        if( customerType) {
            clsName += 'p-0';
        } else {
            clsName += 'iotdevices-container';
        }

        return clsName;
    };

    return (
        <BrowserRouter>
            {!customerType && <CustomPageHeader />}
            <section className={getClsName()}>
                <RbmRoutes />
            </section>
        </BrowserRouter>
    );
};

export default RbmApplication;